import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Heart, ThumbsUp, Sparkles, Smile } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ReactionButtonProps {
  postId?: string;
  commentId?: string;
  initialCount?: number;
}

const reactionTypes = [
  { type: 'heart', icon: Heart, label: 'Heart', color: 'text-red-500' },
  { type: 'support', icon: ThumbsUp, label: 'Support', color: 'text-blue-500' },
  { type: 'helpful', icon: Sparkles, label: 'Helpful', color: 'text-yellow-500' },
  { type: 'smile', icon: Smile, label: 'Smile', color: 'text-green-500' },
];

export function ReactionButton({ postId, commentId, initialCount = 0 }: ReactionButtonProps) {
  const { toast } = useToast();
  const [reactions, setReactions] = useState<any[]>([]);
  const [userReactions, setUserReactions] = useState<string[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getCurrentUser();
    fetchReactions();

    // Subscribe to realtime updates
    const channel = supabase
      .channel(`reactions-${postId || commentId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'community_reactions',
        filter: postId ? `post_id=eq.${postId}` : `comment_id=eq.${commentId}`
      }, () => {
        fetchReactions();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [postId, commentId]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
  };

  const fetchReactions = async () => {
    try {
      let query = supabase
        .from('community_reactions')
        .select('*');

      if (postId) {
        query = query.eq('post_id', postId).is('comment_id', null);
      } else if (commentId) {
        query = query.eq('comment_id', commentId);
      }

      const { data, error } = await query;

      if (error) throw error;

      setReactions(data || []);

      // Check which reactions current user has made
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const myReactions = data?.filter(r => r.user_id === user.id).map(r => r.reaction_type) || [];
        setUserReactions(myReactions);
      }
    } catch (error) {
      console.error('Error fetching reactions:', error);
    }
  };

  const handleReaction = async (reactionType: string) => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to react",
          variant: "destructive"
        });
        return;
      }

      // If user already has this reaction, remove it
      if (userReactions.includes(reactionType)) {
        const { error } = await supabase
          .from('community_reactions')
          .delete()
          .eq('user_id', user.id)
          .eq('reaction_type', reactionType)
          .eq(postId ? 'post_id' : 'comment_id', postId || commentId);

        if (error) throw error;
        setUserReactions(prev => prev.filter(r => r !== reactionType));
      } else {
        // Add new reaction
        const { error } = await supabase
          .from('community_reactions')
          .insert({
            user_id: user.id,
            post_id: postId || null,
            comment_id: commentId || null,
            reaction_type: reactionType
          });

        if (error) throw error;
        setUserReactions(prev => [...prev, reactionType]);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getReactionCounts = () => {
    const counts: Record<string, number> = {};
    reactions.forEach(r => {
      counts[r.reaction_type] = (counts[r.reaction_type] || 0) + 1;
    });
    return counts;
  };

  const reactionCounts = getReactionCounts();
  const totalReactions = reactions.length;

  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="gap-2 h-8 px-3"
            onClick={(e) => e.stopPropagation()}
          >
            {userReactions.length > 0 ? (
              <div className="flex items-center gap-1">
                {reactionTypes
                  .filter(rt => userReactions.includes(rt.type))
                  .map(({ type, icon: Icon, color }) => (
                    <Icon key={type} className={`h-4 w-4 ${color}`} />
                  ))}
              </div>
            ) : (
              <Heart className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="text-sm">{totalReactions}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-1">
            {reactionTypes.map(({ type, icon: Icon, label, color }) => (
              <Button
                key={type}
                variant="ghost"
                size="sm"
                onClick={() => handleReaction(type)}
                className={`flex flex-col items-center gap-1 h-auto py-2 px-3 ${
                  userReactions.includes(type) ? 'bg-secondary' : ''
                }`}
                title={label}
              >
                <Icon className={`h-5 w-5 ${color}`} />
                {reactionCounts[type] > 0 && (
                  <span className="text-xs">{reactionCounts[type]}</span>
                )}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Inline summary of reaction counts by type */}
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        {reactionTypes.map(({ type, icon: Icon, color }) => (
          reactionCounts[type] > 0 ? (
            <div key={type} className="flex items-center gap-1 text-muted-foreground">
              <Icon className={`h-4 w-4 ${color}`} />
              <span className="text-xs">{reactionCounts[type]}</span>
            </div>
          ) : null
        ))}
      </div>
    </div>
  );
}
